import React, { useMemo, useState } from 'react'
import {
  Box,
  Typography,
  Container,
  Grid,
  Button,
  MenuItem,
  Select,
  Link,
  Popover,
  TextField,
  Divider,
  useTheme
} from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import AppsRoundedIcon from '@mui/icons-material/AppsRounded'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded'
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded'
import RestaurantRoundedIcon from '@mui/icons-material/RestaurantRounded'
import CheckroomRoundedIcon from '@mui/icons-material/CheckroomRounded'
import LocalGroceryStoreOutlinedIcon from '@mui/icons-material/LocalGroceryStoreOutlined'
import DevicesOutlinedIcon from '@mui/icons-material/DevicesOutlined'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import LocalPharmacyOutlinedIcon from '@mui/icons-material/LocalPharmacyOutlined'
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip
} from 'chart.js'
import { gql, useQuery } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import {
  getUsers,
  restaurants,
  getVendors,
  getShopTypes,
  getConfiguration
} from '../apollo'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip
)
ChartJS.defaults.color = '#6B7280'
ChartJS.defaults.font.family = 'Inter, sans-serif'

const GET_USERS = gql`
  ${getUsers}
`
const GET_RESTAURANTS = gql`
  ${restaurants}
`
const GET_VENDORS = gql`
  ${getVendors}
`
const GET_CONFIGURATION = gql`
  ${getConfiguration}
`
const GET_SHOP_TYPES = gql`
  ${getShopTypes}
`
// Lean order list used for all the overview maths: totals, the daily trend
// and the category split are all derived from these rows on the client.
const GET_OVERVIEW_ORDERS = gql`
  query OverviewOrders(
    $page: Int
    $rows: Int
    $startDate: String
    $endDate: String
  ) {
    allOrdersPaginated(
      page: $page
      rows: $rows
      search: ""
      startDate: $startDate
      endDate: $endDate
    ) {
      orders {
        _id
        orderId
        createdAt
        orderAmount
        orderStatus
        user {
          _id
          name
        }
        restaurant {
          _id
          name
        }
      }
      totalCount
    }
  }
`

// Delivered orders only, priced the same way as the store invoices: `sales`
// is what the store sold (items, net of refunds), `earnings` is Borku's cut.
const GET_MARKETPLACE_SALES = gql`
  query MarketplaceSales($startDate: String!, $endDate: String!) {
    marketplaceSales(startDate: $startDate, endDate: $endDate) {
      _id
      restaurant
      createdAt
      sales
      earnings
      items {
        food
        title
        quantity
        amount
      }
    }
  }
`

const ORANGE = '#F26A21'
const BLUE = '#2F6FEB'
const MAX_ROWS = 5000
const RANGES = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' }
]
const CATEGORY_COLORS = [
  '#F26A21',
  '#5B8DEF',
  '#6FCF97',
  '#C084FC',
  '#93B4E0',
  '#F2B84B',
  '#B8C0CC'
]
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
]

const startOfDay = date => {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}
const addDays = (date, days) => {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}
const toIsoDay = date => {
  const pad = n => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}`
}
const shortDay = date => `${MONTHS[date.getMonth()]} ${date.getDate()}`
const formatRange = (start, end) =>
  start.getMonth() === end.getMonth()
    ? `${shortDay(start)} – ${end.getDate()}, ${end.getFullYear()}`
    : `${shortDay(start)} – ${shortDay(end)}, ${end.getFullYear()}`
// createdAt arrives either as an epoch string or an ISO date.
const parseDate = raw => {
  if (!raw) return null
  const parsed = new Date(Number.isNaN(Number(raw)) ? raw : Number(raw))
  return Number.isNaN(parsed.getTime()) ? null : parsed
}
const formatMoney = (value, symbol = '£') =>
  `${symbol}${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`
const formatCompactMoney = (value, symbol = '£') =>
  value >= 1000
    ? `${symbol}${Math.round(value / 100) / 10}k`
    : `${symbol}${Math.round(value)}`
const percentChange = (current, previous) => {
  if (!previous) return current ? 100 : 0
  return ((current - previous) / previous) * 100
}
const titleCase = text =>
  String(text || '')
    .toLowerCase()
    .replace(/(^|[\s_-])\w/g, s => s.toUpperCase())
    .replace(/_/g, ' ')

const categoryIcon = name => {
  const key = String(name || '').toLowerCase()
  if (/food|restaurant|meal|kitchen/.test(key)) return RestaurantRoundedIcon
  if (/fashion|cloth|apparel/.test(key)) return CheckroomRoundedIcon
  if (/grocer|mart|super/.test(key)) return LocalGroceryStoreOutlinedIcon
  if (/electr|tech|gadget/.test(key)) return DevicesOutlinedIcon
  if (/home|living|furnit/.test(key)) return HomeOutlinedIcon
  if (/pharm|health|medic/.test(key)) return LocalPharmacyOutlinedIcon
  return MoreHorizRoundedIcon
}

const STATUS_STYLES = {
  PENDING: { color: '#F26A21', bg: '#FFF1E8' },
  ACCEPTED: { color: '#2F6FEB', bg: '#EAF1FF' },
  ASSIGNED: { color: '#2F6FEB', bg: '#EAF1FF' },
  PICKED: { color: '#2F6FEB', bg: '#EAF1FF' },
  DELIVERED: { color: '#16A34A', bg: '#E9F8EF' },
  COMPLETED: { color: '#16A34A', bg: '#E9F8EF' },
  CANCELLED: { color: '#DC2626', bg: '#FDECEC' }
}

const SuperAdminDashboard = props => {
  const { t } = props
  const theme = useTheme()
  // Selected window, both ends inclusive. Presets end today; a custom range
  // can be any From/To pair.
  const [range, setRange] = useState(() => {
    const today = startOfDay(new Date())
    return { start: addDays(today, -6), end: today, preset: 7 }
  })
  const [rangeAnchor, setRangeAnchor] = useState(null)
  const [draftRange, setDraftRange] = useState({ from: '', to: '' })
  const [category, setCategory] = useState('all')
  const [granularity, setGranularity] = useState('daily')

  // The previous window is the same length immediately before the selected
  // one, used for the "vs previous" comparisons.
  const { start, end, prevStart, prevEnd, rangeDays } = useMemo(() => {
    const length = Math.round((range.end - range.start) / 86400000) + 1
    return {
      start: range.start,
      end: range.end,
      prevStart: addDays(range.start, -length),
      prevEnd: addDays(range.start, -1),
      rangeDays: length
    }
  }, [range])
  const openRangePicker = event => {
    setDraftRange({ from: toIsoDay(start), to: toIsoDay(end) })
    setRangeAnchor(event.currentTarget)
  }
  const applyPreset = days => {
    const today = startOfDay(new Date())
    setRange({ start: addDays(today, -(days - 1)), end: today, preset: days })
    setRangeAnchor(null)
  }
  const applyCustomRange = () => {
    // Date inputs give YYYY-MM-DD; parse as local midnight, not UTC.
    const [fy, fm, fd] = draftRange.from.split('-').map(Number)
    const [ty, tm, td] = draftRange.to.split('-').map(Number)
    let from = new Date(fy, fm - 1, fd)
    let to = new Date(ty, tm - 1, td)
    if (from > to) [from, to] = [to, from]
    setRange({ start: from, end: to, preset: null })
    setRangeAnchor(null)
  }
  const customRangeValid =
    /^\d{4}-\d{2}-\d{2}$/.test(draftRange.from) &&
    /^\d{4}-\d{2}-\d{2}$/.test(draftRange.to)

  const { data: vendors, loading: loadingVendors } = useQuery(GET_VENDORS)
  const { data: restData, loading: loadingRest } = useQuery(GET_RESTAURANTS, {
    fetchPolicy: 'network-only'
  })
  const { data: users, loading: loadingUsers } = useQuery(GET_USERS, {
    variables: { page: 0 }
  })
  const { data: shopTypeData } = useQuery(GET_SHOP_TYPES)
  const { data: configData } = useQuery(GET_CONFIGURATION)
  const currency = configData?.configuration?.currencySymbol || '£'
  const money = value => formatMoney(value, currency)
  // Only totalCount matters here: the all-time number of orders.
  const { data: allOrders, loading: loadingAll } = useQuery(
    GET_OVERVIEW_ORDERS,
    { variables: { page: 1, rows: 1 }, fetchPolicy: 'network-only' }
  )
  const { data: currentData, loading: loadingCurrent } = useQuery(
    GET_OVERVIEW_ORDERS,
    {
      variables: {
        page: 1,
        rows: MAX_ROWS,
        startDate: toIsoDay(start),
        endDate: toIsoDay(end)
      },
      fetchPolicy: 'network-only'
    }
  )
  const { data: previousData } = useQuery(GET_OVERVIEW_ORDERS, {
    variables: {
      page: 1,
      rows: MAX_ROWS,
      startDate: toIsoDay(prevStart),
      endDate: toIsoDay(prevEnd)
    },
    fetchPolicy: 'network-only'
  })
  const { data: currentSalesData, loading: loadingSales } = useQuery(
    GET_MARKETPLACE_SALES,
    {
      variables: { startDate: toIsoDay(start), endDate: toIsoDay(end) },
      fetchPolicy: 'network-only'
    }
  )
  const { data: previousSalesData } = useQuery(GET_MARKETPLACE_SALES, {
    variables: { startDate: toIsoDay(prevStart), endDate: toIsoDay(prevEnd) },
    fetchPolicy: 'network-only'
  })
  const { data: recentData, loading: loadingRecent } = useQuery(
    GET_OVERVIEW_ORDERS,
    {
      variables: { page: 1, rows: 50 },
      fetchPolicy: 'network-only'
    }
  )

  const restaurantList = restData?.restaurants || []
  // restaurant id -> shop type slug, and slug -> display title
  const restaurantType = useMemo(() => {
    const map = {}
    restaurantList.forEach(r => {
      map[r._id] = r.shopType || 'other'
    })
    return map
  }, [restaurantList])
  const typeTitles = useMemo(() => {
    const map = {}
    ;(shopTypeData?.shopTypes || []).forEach(s => {
      map[s.slug] = s.title
    })
    return map
  }, [shopTypeData])
  const categoryName = slug => typeTitles[slug] || titleCase(slug || 'Other')
  const categoryOptions = useMemo(() => {
    const slugs = new Set(restaurantList.map(r => r.shopType || 'other'))
    return [...slugs].map(slug => ({ slug, title: categoryName(slug) }))
  }, [restaurantList, typeTitles])

  const orderType = order => restaurantType[order.restaurant?._id] || 'other'
  const inCategory = order =>
    category === 'all' || orderType(order) === category

  const currentOrders = (currentData?.allOrdersPaginated?.orders || []).filter(
    inCategory
  )
  const previousOrders = (
    previousData?.allOrdersPaginated?.orders || []
  ).filter(inCategory)
  const saleInCategory = row =>
    category === 'all' ||
    (restaurantType[row.restaurant] || 'other') === category
  const currentSales = (currentSalesData?.marketplaceSales || []).filter(
    saleInCategory
  )
  const previousSales = (previousSalesData?.marketplaceSales || []).filter(
    saleInCategory
  )
  const sum = (rows, key) => rows.reduce((s, row) => s + (row[key] || 0), 0)
  const uniqueCount = (orders, pick) =>
    new Set(orders.map(pick).filter(Boolean)).size

  const grossSales = sum(currentSales, 'sales')
  const salesChange = percentChange(grossSales, sum(previousSales, 'sales'))
  const borkuEarnings = sum(currentSales, 'earnings')
  const earningsChange = percentChange(
    borkuEarnings,
    sum(previousSales, 'earnings')
  )
  const ordersChange = percentChange(
    currentOrders.length,
    previousOrders.length
  )
  const customersInPeriod = uniqueCount(currentOrders, o => o.user?._id)
  const customersChange = percentChange(
    customersInPeriod,
    uniqueCount(previousOrders, o => o.user?._id)
  )

  // One bucket per day of the window, used by the main chart and sparklines.
  const days = useMemo(() => {
    const list = []
    for (let d = new Date(start); d <= end; d = addDays(d, 1)) {
      list.push(new Date(d))
    }
    return list
  }, [start, end])
  const daily = useMemo(() => {
    const buckets = days.map(day => ({
      day,
      sales: 0,
      earnings: 0,
      orders: 0,
      stores: new Set(),
      customers: new Set()
    }))
    const index = {}
    buckets.forEach((b, i) => {
      index[toIsoDay(b.day)] = i
    })
    currentOrders.forEach(order => {
      const created = parseDate(order.createdAt)
      if (!created) return
      const bucket = buckets[index[toIsoDay(created)]]
      if (!bucket) return
      bucket.orders += 1
      if (order.restaurant?._id) bucket.stores.add(order.restaurant._id)
      if (order.user?._id) bucket.customers.add(order.user._id)
    })
    currentSales.forEach(row => {
      const created = parseDate(row.createdAt)
      if (!created) return
      const bucket = buckets[index[toIsoDay(created)]]
      if (!bucket) return
      bucket.sales += row.sales
      bucket.earnings += row.earnings
    })
    return buckets
  }, [days, currentOrders, currentSales])

  const chartSeries = useMemo(() => {
    if (granularity === 'daily') {
      return daily.map(b => ({
        label: shortDay(b.day),
        value: b.sales,
        earnings: b.earnings
      }))
    }
    const weeks = []
    for (let i = 0; i < daily.length; i += 7) {
      const chunk = daily.slice(i, i + 7)
      weeks.push({
        label: shortDay(chunk[0].day),
        value: chunk.reduce((s, b) => s + b.sales, 0),
        earnings: chunk.reduce((s, b) => s + b.earnings, 0)
      })
    }
    return weeks
  }, [daily, granularity])

  const categorySplit = useMemo(() => {
    const totals = {}
    currentSales.forEach(row => {
      const slug = restaurantType[row.restaurant] || 'other'
      totals[slug] = (totals[slug] || 0) + row.sales
    })
    const sum = Object.values(totals).reduce((s, v) => s + v, 0)
    // Every active shop type is listed (at 0% when it had no sales), plus any
    // type a store uses that is missing from the Shop Types list.
    const slugs = new Set([
      ...(shopTypeData?.shopTypes || [])
        .filter(type => type.isActive !== false)
        .map(type => type.slug),
      ...restaurantList.map(r => r.shopType || 'other'),
      ...Object.keys(totals)
    ])
    return [...slugs]
      .filter(slug => category === 'all' || slug === category)
      .map(slug => {
        const amount = totals[slug] || 0
        return {
          slug,
          amount,
          name: categoryName(slug),
          percent: sum ? Math.round((amount / sum) * 100) : 0
        }
      })
      .sort((a, b) => b.amount - a.amount || a.name.localeCompare(b.name))
  }, [
    currentSales,
    typeTitles,
    restaurantType,
    shopTypeData,
    restaurantList,
    category
  ])
  const categoryTotal = categorySplit.reduce((s, row) => s + row.amount, 0)

  // Top 5 stores and products by delivered sales in the window, for picking
  // promo candidates.
  const restaurantNames = useMemo(() => {
    const map = {}
    restaurantList.forEach(r => {
      map[r._id] = r.name
    })
    return map
  }, [restaurantList])
  const topStores = useMemo(() => {
    const totals = {}
    currentSales.forEach(row => {
      const entry = totals[row.restaurant] || {
        id: row.restaurant,
        name: restaurantNames[row.restaurant] || t('Unknown store'),
        amount: 0,
        orders: 0
      }
      entry.amount += row.sales
      entry.orders += 1
      totals[row.restaurant] = entry
    })
    return Object.values(totals)
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5)
  }, [currentSales, restaurantNames])
  const topProducts = useMemo(() => {
    const totals = {}
    currentSales.forEach(row => {
      ;(row.items || []).forEach(item => {
        const key = `${row.restaurant}:${item.food}`
        const entry = totals[key] || {
          id: key,
          name: item.title,
          store: restaurantNames[row.restaurant] || t('Unknown store'),
          amount: 0,
          quantity: 0
        }
        entry.amount += item.amount
        entry.quantity += item.quantity
        totals[key] = entry
      })
    })
    return Object.values(totals)
      .sort((a, b) => b.amount - a.amount || b.quantity - a.quantity)
      .slice(0, 5)
  }, [currentSales, restaurantNames])

  const recentOrders = (recentData?.allOrdersPaginated?.orders || [])
    .filter(inCategory)
    .slice(0, 5)

  const activeStores = restaurantList.filter(r => r.isActive).length
  const storesWithOrders = uniqueCount(currentOrders, o => o.restaurant?._id)
  const periodLabel =
    rangeDays === 7 ? t('vs previous week') : t('vs previous period')

  const stats = [
    {
      label: t('Gross sales'),
      value: loadingSales ? '...' : money(grossSales),
      icon: BarChartRoundedIcon,
      color: '#F26A21',
      tint: '#FFF1E8',
      change: salesChange,
      caption: t('store sales, delivered orders'),
      spark: daily.map(b => b.sales)
    },
    {
      label: t('Borku earnings'),
      value: loadingSales ? '...' : money(borkuEarnings),
      icon: PaymentsOutlinedIcon,
      color: BLUE,
      tint: '#EAF1FF',
      change: earningsChange,
      caption: t('commission + fees − credits'),
      spark: daily.map(b => b.earnings)
    },
    {
      label: t('Orders'),
      value: loadingCurrent ? '...' : currentOrders.length.toLocaleString(),
      icon: ShoppingCartOutlinedIcon,
      color: '#0EA5E9',
      tint: '#E7F6FD',
      change: ordersChange,
      caption: loadingAll
        ? ''
        : `${(
            allOrders?.allOrdersPaginated?.totalCount ?? 0
          ).toLocaleString()} ${t('all time')}`,
      spark: daily.map(b => b.orders)
    },
    {
      label: t('Active stores'),
      value: loadingRest ? '...' : activeStores.toLocaleString(),
      icon: StorefrontOutlinedIcon,
      color: '#16A34A',
      tint: '#E9F8EF',
      note: `${storesWithOrders} ${t('with orders')}`,
      spark: daily.map(b => b.stores.size)
    },
    {
      label: t('Total vendors'),
      value: loadingVendors
        ? '...'
        : (vendors?.vendors?.length ?? 0).toLocaleString(),
      icon: BusinessCenterOutlinedIcon,
      color: '#0EA5A4',
      tint: '#E6F7F6',
      note: t('Registered vendors')
    },
    {
      label: t('Customers'),
      value: loadingUsers
        ? '...'
        : (users?.users?.length ?? 0).toLocaleString(),
      icon: PeopleAltOutlinedIcon,
      color: '#8B5CF6',
      tint: '#F3EEFF',
      change: customersChange,
      caption: t('ordering customers'),
      spark: daily.map(b => b.customers.size)
    }
  ]

  const chartData = {
    labels: chartSeries.map(p => p.label),
    datasets: [
      {
        data: chartSeries.map(p => p.value),
        borderColor: ORANGE,
        borderWidth: 2,
        tension: 0.35,
        fill: true,
        pointRadius: chartSeries.length > 14 ? 0 : 4,
        pointHoverRadius: 5,
        pointBackgroundColor: ORANGE,
        pointBorderColor: ORANGE,
        label: t('Gross sales'),
        backgroundColor: context => {
          const { ctx, chartArea } = context.chart
          if (!chartArea) return 'rgba(242,106,33,0.12)'
          const gradient = ctx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
          )
          gradient.addColorStop(0, 'rgba(242,106,33,0.22)')
          gradient.addColorStop(1, 'rgba(242,106,33,0.02)')
          return gradient
        }
      },
      {
        label: t('Borku earnings'),
        data: chartSeries.map(p => p.earnings),
        borderColor: BLUE,
        borderWidth: 2,
        tension: 0.35,
        fill: false,
        pointRadius: chartSeries.length > 14 ? 0 : 4,
        pointHoverRadius: 5,
        pointBackgroundColor: BLUE,
        pointBorderColor: BLUE
      }
    ]
  }
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: ctx => `${ctx.dataset.label}: ${money(ctx.parsed.y)}`
        }
      }
    },
    scales: {
      x: {
        grid: { color: '#EEF0F4' },
        border: { display: false },
        ticks: { maxTicksLimit: 8 }
      },
      y: {
        beginAtZero: true,
        // Keeps a readable £0–£100 scale when the period has no sales
        suggestedMax: chartSeries.some(p => p.value > 0) ? undefined : 100,
        grid: { color: '#EEF0F4' },
        border: { display: false },
        ticks: {
          maxTicksLimit: 5,
          precision: 0,
          callback: v => formatCompactMoney(v, currency)
        }
      }
    }
  }

  const selectSx = {
    height: 44,
    minWidth: 190,
    borderRadius: '12px',
    bgcolor: 'common.white',
    fontSize: 14,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' }
  }

  return (
    <Container fluid sx={{ pt: 1 }}>
      {/* Heading row */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3
        }}>
        <Box>
          <Typography
            sx={{
              fontSize: { xs: 24, sm: 28 },
              fontWeight: 800,
              color: 'text.primary',
              letterSpacing: '-0.02em'
            }}>
            {t('Marketplace overview')}
          </Typography>
          <Typography sx={{ fontSize: 14, color: 'text.disabled', mt: 0.5 }}>
            {t('A clear view of your business, all in one place.')}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 2
          }}>
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon sx={{ fontSize: 18 }} />}
            onClick={() => window.open('https://borku.co.uk/', '_blank')}
            sx={{
              height: 44,
              px: 2.5,
              borderRadius: '12px',
              borderColor: ORANGE,
              color: ORANGE,
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { borderColor: ORANGE, bgcolor: '#FFF6F0' }
            }}>
            {t('View website')}
          </Button>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Select
              value={category}
              onChange={e => setCategory(e.target.value)}
              sx={selectSx}
              renderValue={value => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                  <AppsRoundedIcon sx={{ fontSize: 20 }} />
                  {value === 'all' ? t('All categories') : categoryName(value)}
                </Box>
              )}>
              <MenuItem value="all">{t('All categories')}</MenuItem>
              {categoryOptions.map(option => (
                <MenuItem key={option.slug} value={option.slug}>
                  {option.title}
                </MenuItem>
              ))}
            </Select>
            <Select
              value="range"
              open={false}
              onOpen={openRangePicker}
              sx={selectSx}
              renderValue={() => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                  <CalendarTodayOutlinedIcon sx={{ fontSize: 18 }} />
                  {formatRange(start, end)}
                </Box>
              )}>
              <MenuItem value="range" />
            </Select>
            <Popover
              open={Boolean(rangeAnchor)}
              anchorEl={rangeAnchor}
              onClose={() => setRangeAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { mt: 1, borderRadius: '12px', width: 300 } }}>
              <Box sx={{ py: 1 }}>
                {RANGES.map(option => (
                  <MenuItem
                    key={option.value}
                    selected={range.preset === option.value}
                    onClick={() => applyPreset(option.value)}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </Box>
              <Divider />
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5
                }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  {t('Custom range')}
                </Typography>
                <TextField
                  label={t('From')}
                  type="date"
                  size="small"
                  value={draftRange.from}
                  onChange={e =>
                    setDraftRange(d => ({ ...d, from: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: toIsoDay(new Date()) }}
                />
                <TextField
                  label={t('To')}
                  type="date"
                  size="small"
                  value={draftRange.to}
                  onChange={e =>
                    setDraftRange(d => ({ ...d, to: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: toIsoDay(new Date()) }}
                />
                <Button
                  variant="contained"
                  disabled={!customRangeValid}
                  onClick={applyCustomRange}
                  sx={{
                    bgcolor: ORANGE,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '10px',
                    '&:hover': { bgcolor: '#D95A16' }
                  }}>
                  {t('Apply')}
                </Button>
              </Box>
            </Popover>
          </Box>
        </Box>
      </Box>

      {/* KPI cards */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
            xl: 'repeat(6, 1fr)'
          }
        }}>
        {stats.map(stat => (
          <StatCard key={stat.label} {...stat} />
        ))}
      </Box>

      <Grid container spacing={2} mt={0.5}>
        {/* Sales overview */}
        <Grid item xs={12} lg={8}>
          <SurfaceCard>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start'
              }}>
              <Box>
                <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
                  {t('Sales overview')}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mt: 1,
                    flexWrap: 'wrap'
                  }}>
                  <Typography
                    sx={{
                      fontSize: 32,
                      fontWeight: 800,
                      letterSpacing: '-0.02em'
                    }}>
                    {money(grossSales)}
                  </Typography>
                  <Change value={salesChange} large />
                  <Typography sx={{ fontSize: 14, color: 'text.disabled' }}>
                    {periodLabel}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2.5,
                    mt: 1,
                    flexWrap: 'wrap',
                    fontSize: 13,
                    color: 'text.secondary'
                  }}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: ORANGE
                      }}
                    />
                    {t('Gross sales')} ({t('items sold by stores')})
                  </Box>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: BLUE
                      }}
                    />
                    {t('Borku earnings')}: {money(borkuEarnings)}
                  </Box>
                </Box>
              </Box>
              <Select
                value={granularity}
                onChange={e => setGranularity(e.target.value)}
                size="small"
                sx={{ ...selectSx, minWidth: 110, height: 40 }}>
                <MenuItem value="daily">{t('Daily')}</MenuItem>
                <MenuItem value="weekly">{t('Weekly')}</MenuItem>
              </Select>
            </Box>
            <Box sx={{ height: { xs: 240, md: 280 }, mt: 2 }}>
              <Line data={chartData} options={chartOptions} />
            </Box>
          </SurfaceCard>
        </Grid>

        {/* Sales by category */}
        <Grid item xs={12} lg={4}>
          <SurfaceCard>
            <Typography sx={{ fontSize: 20, fontWeight: 700, mb: 2.5 }}>
              {t('Sales by category')}
            </Typography>
            {categorySplit.length === 0 ? (
              <Typography sx={{ fontSize: 14, color: 'text.disabled' }}>
                {loadingCurrent ? t('Loading...') : t('No shop types yet')}
              </Typography>
            ) : (
              categorySplit.map((row, i) => {
                const Icon = categoryIcon(row.slug + ' ' + row.name)
                const color = CATEGORY_COLORS[i % CATEGORY_COLORS.length]
                return (
                  <Box
                    key={row.slug}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '32px 96px 1fr 40px',
                      alignItems: 'center',
                      gap: 1.5,
                      mb: 2
                    }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        bgcolor: `${color}1F`,
                        color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                      <Icon sx={{ fontSize: 18 }} />
                    </Box>
                    <Typography noWrap sx={{ fontSize: 14 }}>
                      {row.name}
                    </Typography>
                    <Box
                      sx={{
                        height: 18,
                        borderRadius: '5px',
                        bgcolor: '#F1F3F7',
                        overflow: 'hidden'
                      }}>
                      <Box
                        sx={{
                          width: row.amount
                            ? `${Math.max(row.percent, 2)}%`
                            : 0,
                          height: '100%',
                          borderRadius: '5px',
                          bgcolor: color
                        }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: 14, textAlign: 'right' }}>
                      {row.percent}%
                    </Typography>
                  </Box>
                )
              })
            )}
            {!loadingCurrent && categorySplit.length > 0 && !categoryTotal ? (
              <Typography sx={{ fontSize: 13, color: 'text.disabled', mt: 1 }}>
                {t('No sales in this period')}
              </Typography>
            ) : null}
          </SurfaceCard>
        </Grid>
      </Grid>

      {/* Top stores and products */}
      <Grid container spacing={2} mt={0.5}>
        <Grid item xs={12} lg={6}>
          <RankCard
            title={t('Top stores')}
            subtitle={t('By delivered sales in this period')}
            rows={topStores.map(row => ({
              id: row.id,
              name: row.name,
              detail: `${row.orders} ${t('orders')}`,
              value: money(row.amount)
            }))}
            empty={
              loadingSales ? t('Loading...') : t('No sales in this period')
            }
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <RankCard
            title={t('Top products')}
            subtitle={t('By delivered sales in this period')}
            rows={topProducts.map(row => ({
              id: row.id,
              name: row.name,
              detail: `${row.store} · ${row.quantity} ${t('sold')}`,
              value: money(row.amount)
            }))}
            empty={
              loadingSales ? t('Loading...') : t('No sales in this period')
            }
          />
        </Grid>
      </Grid>

      {/* Recent orders */}
      <Box sx={{ mt: 2, mb: 3 }}>
        <SurfaceCard>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2
            }}>
            <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
              {t('Recent orders')}
            </Typography>
            <Link
              href="#/super_admin/orders"
              underline="hover"
              sx={{ color: ORANGE, fontWeight: 600, fontSize: 15 }}>
              {t('View all orders')}
            </Link>
          </Box>
          <Box sx={{ overflowX: 'auto' }}>
            <Box
              component="table"
              sx={{
                width: '100%',
                minWidth: 720,
                borderCollapse: 'collapse',
                '& th': {
                  textAlign: 'left',
                  fontSize: 13,
                  fontWeight: 500,
                  color: 'text.disabled',
                  bgcolor: '#F6F7FA',
                  px: 1.5,
                  py: 1.25
                },
                '& th:first-of-type': { borderRadius: '10px 0 0 10px' },
                '& th:last-of-type': { borderRadius: '0 10px 10px 0' },
                '& td': {
                  fontSize: 14,
                  px: 1.5,
                  py: 1.6,
                  borderBottom: `1px solid ${theme.palette.divider}`
                }
              }}>
              <thead>
                <tr>
                  <th>{t('Order')}</th>
                  <th>{t('Customer')}</th>
                  <th>{t('Store')}</th>
                  <th>{t('Category')}</th>
                  <th>{t('Total')}</th>
                  <th>{t('Status')}</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map(order => (
                  <tr key={order._id}>
                    <td style={{ fontWeight: 700 }}>#{order.orderId}</td>
                    <td>{order.user?.name || 'N/A'}</td>
                    <td>{order.restaurant?.name || 'N/A'}</td>
                    <td>{categoryName(orderType(order))}</td>
                    <td style={{ fontWeight: 600 }}>
                      {money(order.orderAmount)}
                    </td>
                    <td>
                      <StatusPill status={order.orderStatus} />
                    </td>
                  </tr>
                ))}
                {!loadingRecent && recentOrders.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center' }}>
                      {t('NoOrdersYet')}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </Box>
          </Box>
        </SurfaceCard>
      </Box>
    </Container>
  )
}

const SurfaceCard = ({ children }) => (
  <Box
    sx={{
      p: 3,
      borderRadius: '16px',
      bgcolor: 'common.white',
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
      height: '100%'
    }}>
    {children}
  </Box>
)

// Numbered top-5 list used for the top stores and top products cards.
const RankCard = ({ title, subtitle, rows, empty }) => (
  <SurfaceCard>
    <Typography sx={{ fontSize: 20, fontWeight: 700 }}>{title}</Typography>
    <Typography sx={{ fontSize: 13, color: 'text.disabled', mb: 2 }}>
      {subtitle}
    </Typography>
    {rows.length === 0 ? (
      <Typography sx={{ fontSize: 14, color: 'text.disabled' }}>
        {empty}
      </Typography>
    ) : (
      rows.map((row, i) => (
        <Box
          key={row.id}
          sx={{
            display: 'grid',
            gridTemplateColumns: '28px 1fr auto',
            alignItems: 'center',
            gap: 1.5,
            py: 1.25,
            borderTop: i ? '1px solid' : 'none',
            borderColor: 'divider'
          }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              bgcolor: i === 0 ? '#FFF1E8' : '#F1F3F7',
              color: i === 0 ? ORANGE : 'text.secondary',
              fontSize: 13,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            {i + 1}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 14, fontWeight: 600 }}>
              {row.name}
            </Typography>
            <Typography noWrap sx={{ fontSize: 12, color: 'text.disabled' }}>
              {row.detail}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
            {row.value}
          </Typography>
        </Box>
      ))
    )}
  </SurfaceCard>
)

const Change = ({ value, large }) => {
  const up = value >= 0
  const Arrow = up ? ArrowUpwardRoundedIcon : ArrowDownwardRoundedIcon
  const color = up ? '#16A34A' : '#DC2626'
  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, color }}>
      <Arrow sx={{ fontSize: large ? 20 : 14 }} />
      <Typography
        component="span"
        sx={{ fontSize: large ? 16 : 13, fontWeight: 600, color }}>
        {up ? '+' : ''}
        {value.toFixed(1)}%
      </Typography>
    </Box>
  )
}

// Tiny trend line in the corner of a KPI card.
const Sparkline = ({ values, color }) => {
  if (!values || values.length < 2) return null
  const width = 64
  const height = 28
  const max = Math.max(...values, 1)
  const step = width / (values.length - 1)
  const points = values
    .map((v, i) => `${i * step},${height - 2 - (v / max) * (height - 4)}`)
    .join(' ')
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      height={height}
      style={{ flex: '0 1 64px', minWidth: 0, maxWidth: width }}>
      <polyline
        points={`0,${height} ${points} ${width},${height}`}
        fill={color}
        opacity={0.08}
      />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

const StatCard = ({
  label,
  value,
  icon: Icon,
  color,
  tint,
  change,
  caption,
  note,
  spark
}) => (
  <Box
    sx={{
      p: 2,
      borderRadius: '16px',
      bgcolor: 'common.white',
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
      display: 'flex',
      gap: 1.75,
      minWidth: 0
    }}>
    <Box
      sx={{
        flexShrink: 0,
        width: 48,
        height: 48,
        borderRadius: '12px',
        bgcolor: tint,
        color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
      <Icon sx={{ fontSize: 24 }} />
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
        {label}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1
        }}>
        <Typography
          noWrap
          sx={{
            flexShrink: 0,
            fontSize: 24,
            fontWeight: 800,
            color: 'text.primary',
            letterSpacing: '-0.02em'
          }}>
          {value}
        </Typography>
        <Sparkline values={spark} color={color} />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
        {typeof change === 'number' ? <Change value={change} /> : null}
        <Typography
          noWrap
          title={caption || note}
          sx={{ fontSize: 12, color: 'text.disabled' }}>
          {caption || note}
        </Typography>
      </Box>
    </Box>
  </Box>
)

const StatusPill = ({ status }) => {
  const style = STATUS_STYLES[status] || { color: '#6B7280', bg: '#F1F3F7' }
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.6,
        borderRadius: '8px',
        bgcolor: style.bg,
        color: style.color,
        fontSize: 14,
        fontWeight: 500
      }}>
      <Box
        component="span"
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: style.color
        }}
      />
      {titleCase(status)}
    </Box>
  )
}

export default withTranslation()(SuperAdminDashboard)
